import React from "react";
import { Sparkles, TrendingUp, ShieldAlert, ArrowUpRight, History } from "lucide-react";
import { parseExplain, OPP_MODEL } from "../../lib/opportunityApi";
import { cn } from "../../lib/utils";

const LEVEL = {
  HIGH: "bg-critical/10 text-critical border-critical/30",
  MEDIUM: "bg-warning/10 text-warning border-warning/30",
  LOW: "bg-info/10 text-info border-info/30",
};

const Pulse = () => <span className="inline-block w-1.5 h-3.5 align-middle ml-0.5 bg-primary/70 animate-pulse" aria-hidden />;

function Lines({ title, icon: Icon, items, testid, tone = "text-primary" }) {
  if (!items.length) return null;
  return (
    <div>
      <div className="kpi-label text-[10px] mb-2">{title}</div>
      <ul className="space-y-2" data-testid={testid}>
        {items.map((t, i) => (
          <li key={i} data-testid={`${testid}-${i}`} className="flex gap-2.5 text-[13px] leading-relaxed animate-fade-in">
            <Icon size={13} className={cn("shrink-0 mt-1", tone)} /><span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function First({ items }) {
  if (!items.length) return null;
  return (
    <div>
      <div className="kpi-label text-[10px] mb-2">Act on first</div>
      <div className="space-y-2" data-testid="opp-ai-first">
        {items.map((r, i) => (
          <div key={i} data-testid={`opp-ai-first-${i}`} className="tile px-3.5 py-3 flex gap-3 items-start animate-fade-in">
            <span className={cn("shrink-0 text-[10px] font-semibold uppercase tracking-wider border rounded-sm px-1.5 py-0.5", LEVEL[r.level] || "bg-surface-2 text-muted border-border")}>{r.level || "Next"}</span>
            <span className="text-[13px] leading-relaxed flex-1">{r.text}</span>
            <ArrowUpRight size={13} className="text-subtle shrink-0 mt-1" />
          </div>
        ))}
      </div>
    </div>
  );
}

// The AI explanation: summary, what drives the total, what to act on first, what could overstate it and the change since the last cycle.
export function OpportunityAnswer({ text, streaming }) {
  const a = parseExplain(text);
  const empty = !a.summary && !a.drivers.length;
  return (
    <section data-testid="opp-answer" className="space-y-5">
      <div className="border-l-2 border-primary pl-3.5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold mb-1 inline-flex items-center gap-1.5"><Sparkles size={11} /> In plain words</div>
        <p data-testid="opp-ai-summary" className="text-[14px] leading-relaxed">
          {empty ? <span className="text-muted">{OPP_MODEL} is reading the cycle…</span> : a.summary}
          {streaming && <Pulse />}
        </p>
      </div>
      <Lines title="What's driving it" icon={TrendingUp} items={a.drivers} testid="opp-ai-drivers" />
      <First items={a.first} />
      <Lines title="What could overstate it" icon={ShieldAlert} items={a.cautions} testid="opp-ai-cautions" tone="text-warning" />
      <Lines title="Since the last cycle" icon={History} items={a.change} testid="opp-ai-change" tone="text-info" />
    </section>
  );
}
